import Slider from "@mui/material/Slider"
import { useEffect, useState } from "react"
import defaultColors from "../../../../../theme/colors.json"
import { SliderQuestion } from "~/types/questionTypes"
import { parseFieldAnnotations } from "~/util/parseFieldAnnotations"

type SliderFieldProps = {
  value: string | string[]
  question: SliderQuestion
  onChange: (newValue: string | string[]) => void
}

export const SliderField = (props: SliderFieldProps): JSX.Element => {
  const { value, question, onChange } = props

  const {
    textValidationMax,
    textValidationMin,
    textValidationTypeOrShowSliderNumber
  } = question

  // Redcap defaults to 0 for min and 100 for max.
  // When min/max are set to their corresponding defaults, the API sends back empty strings.
  const minVal = textValidationMin === "" ? 0 : parseInt(textValidationMin)
  const maxVal = textValidationMax === "" ? 100 : parseInt(textValidationMax)
  const [currentValue, setCurrentValue] = useState<number>(
    Math.ceil(maxVal / 2)
  )

  const { showSliderValue } = parseFieldAnnotations(question.fieldAnnotation)

  const labelSpecs = question.selectChoicesOrCalculations.split("|")
  // Remove numbers before/after from the labels
  const isNumber = (numberString: string | undefined) =>
    !isNaN(parseInt(numberString || ""))

  const labels = labelSpecs
    .map((labelSpec) => {
      const parts = labelSpec.trim().split(" ")
      if (isNumber(parts.at(0))) {
        parts.shift()
      } else if (isNumber(parts.at(-1))) {
        parts.pop()
      }
      return parts.join(" ")
    })
    .filter(Boolean)

  interface Mark {
    value: number
    label: number | string
    "data-chosen"?: boolean
  }

  const handleMarkUpdate = (marks: Mark[], currentValue: number) => {
    return marks.map((mark: Mark) => ({
      ...mark,
      "data-chosen": mark.value === currentValue
    }))
  }

  useEffect(() => {
    onChange(String(currentValue))
    const marks = document.querySelectorAll(".MuiSlider-markLabel")
    marks.forEach((mark) => {
      const dataIndex = mark.getAttribute("data-index")
      if (Number(dataIndex) + 1 === currentValue) {
        mark.setAttribute("data-chosen", "true")
      } else {
        mark.removeAttribute("data-chosen")
      }
    })
  }, [currentValue])
  return (
    <div className="flex flex-col gap-14">
      <p>Træk markør til det passende sted på skalaen</p>
      <div>
        <Slider
          className={`${value ? "slider-valid" : ""}`}
          aria-label="Slider"
          value={value ? parseInt(value as string) : Math.ceil(maxVal / 2)}
          step={1}
          marks={
            textValidationTypeOrShowSliderNumber === "text"
              ? handleMarkUpdate(
                  [
                    ...Array(maxVal)
                      .fill(0)
                      .map((_, i) => ({ value: i + 1, label: i + 1 }))
                  ],
                  currentValue
                )
              : []
          }
          min={minVal}
          max={maxVal}
          onChange={(_, newValue) => {
            setCurrentValue(newValue as number)
            onChange(String(newValue))
          }}
          valueLabelDisplay={showSliderValue ? "on" : "off"}
          sx={{
            color: `var(--color-primary-500, ${defaultColors.primary["500"]})`,
            "& .MuiSlider-thumb": {
              color: `var(--color-primary-700, ${defaultColors.primary["700"]})`
            },
            "& .MuiSlider-track": {
              height: "10px"
            },
            "& .MuiSlider-rail": {
              color: `var(--color-neutral-400, ${defaultColors.neutral["400"]})`
            },
            '& .MuiSlider-markLabel[data-chosen="true"]': {
              color: `var(--color-neutral-900, ${defaultColors.neutral["900"]})`,
              fontWeight: "bold"
            }
          }}
          data-index={currentValue}
        />
        <div className="flex flex-row justify-between">
          {labels.map((label) => (
            <p key={`slider-label-${label}`}>{label}</p>
          ))}
        </div>
        <input
          className="invisible"
          type="number"
          name="slider-valid-input"
          value={value ?? ""}
          readOnly
          required
        />
      </div>
    </div>
  )
}
